import type { UseFormReturn } from 'react-hook-form';
import { useState } from 'react';
import { faPalette } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Checkbox, Labeled, TextInputField } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IFormMode } from '~/helpers/types';
import type { ICategoryFormValues } from './CategoryForm';
import { FormField } from '~/components/atoms/FormField';
import { FormSection } from '~/components/atoms/FormSection';
import { ImageCreatorFormDialog } from '~/components/forms/ImageCreatorForm';
import { CategorySelector } from '~/components/organisms/fields/CategorySelector';
import { FormFileUploader } from '~/components/organisms/fields/FileUploader';
import { useAppQuery } from '~/lib/api/modules/Apps/hooks/useAppQuery';

export type ICategoryFormMainSectionProps = IContainerProps & {
  form: UseFormReturn<ICategoryFormValues>;
  mode: IFormMode;
  appId: string;
  categoryId?: string;
};

export const CategoryFormMainSection: React.FC<
  ICategoryFormMainSectionProps
> = (props) => {
  const { sx, form, appId, categoryId } = props;
  const { t } = useTranslation();
  const [imageCreatorOpened, setImageCreatorOpened] = useState(false);
  const appQuery = useAppQuery({ appId });

  return (
    <>
      <ImageCreatorFormDialog
        opened={imageCreatorOpened}
        onClose={() => setImageCreatorOpened(false)}
        appId={appId}
        stageSize={{
          width: 540,
          height: 300,
        }}
        defaultValues={{
          backgroundColor:
            appQuery.data?.settings?.press?.env?.appThemeColorPrimary,
        }}
        onSuccess={(id) => {
          form.setValue('pictures', [
            {
              type: 'picture',
              id,
            },
          ]);
        }}
      />

      <FormSection sx={sx}>
        <Labeled label={t('common.name')} required>
          <FormField
            as={TextInputField}
            forwardError
            control={form.control}
            name="name"
          />
        </Labeled>

        <Labeled label={t('CategoryFormMainSection.field.picture.label')}>
          <FormFileUploader
            control={form.control}
            name="pictures"
            appId={appId}
            maxFiles={1}
            acceptTypes={['picture']}
            supportingText={t(
              'CategoryFormMainSection.field.picture.supporting-text',
            )}
            extraActions={
              <Button
                variant="text"
                onClick={() => setImageCreatorOpened(true)}
                icon={<FontAwesomeIcon icon={faPalette} />}
                loading={appQuery.isLoading}
              >
                {t('CategoryFormMainSection.designImage')}
              </Button>
            }
          />
        </Labeled>
        <Labeled
          label={t('CategoryFormMainSection.field.parent-category.label')}
        >
          <FormField
            as={CategorySelector}
            forwardError
            control={form.control}
            name="parentId"
            appId={appId}
            filter={{
              includeTypes: ['category'],
              excludeIds: categoryId ? [categoryId] : undefined,
            }}
            maxDepth={1}
            clearable
          />
        </Labeled>
        <Labeled
          label={t('CategoryFormMainSection.field.event.label')}
          supportingText={t(
            'CategoryFormMainSection.field.event.supporting-text',
          )}
          labelPosition="right"
        >
          <FormField as={Checkbox} control={form.control} name="isEvent" />
        </Labeled>
      </FormSection>
    </>
  );
};
